import * as React from 'react'

import { Box, Button, CollapsableCard, Icon, Text } from '@factorialco/deprecated-design-system'

import Separator from 'modules/core/components/Separator'
import i18n from 'modules/core/lib/i18n'

import ContactModal from 'modules/billing/components/ContactModalPage'
import useNewContactRequest from 'modules/onboarding/hooks/useNewContactRequest'

type Props = {
  question: string
  answer: string | React.ReactNode
}

const FaqItem = ({ question, answer }: Props) => {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <Box
      gridColSpan={{ xxs: 12 }}
      onClick={() => {
        setIsOpen(!isOpen)
      }}
    >
      <CollapsableCard
        contentBackground="grey000White"
        Card={
          <Box
            cursor="pointer"
            flexDirection="row"
            justifyContent="spaceBetween"
            alignItems="center"
            paddingX="s8"
          >
            <Text size="300" weight="medium">
              {question}
            </Text>
            <Icon.Medium icon={isOpen ? 'chevron-up' : 'chevron-down'} />
          </Box>
        }
        Content={<Text size="200">{answer}</Text>}
        open={isOpen}
      />
    </Box>
  )
}

const Header = () => {
  return (
    <Box paddingY="s32" gap="s16" alignItems="center">
      <Icon.Large icon="window-user" />
      <Box gap="s8" alignItems="center">
        <Text size="400" weight="medium" color="grey1000">
          {i18n.t('pricing_plans.custom_bundles.faq.title')}
        </Text>
        <Text size="300">{i18n.t('pricing_plans.custom_bundles.faq.description')}</Text>
      </Box>
      <Separator compact />
    </Box>
  )
}

const PricingFaq = () => {
  const [showModal, setShowModal] = React.useState(false)
  const { action, eventEmitter } = useNewContactRequest({
    flashError: true,
    flashSuccess: false,
  })
  const faqItems = [
    {
      question: i18n.t('pricing_plans.custom_bundles.faq.question_1'),
      answer: i18n.t('pricing_plans.custom_bundles.faq.answer_1'),
    },
    {
      question: i18n.t('pricing_plans.custom_bundles.faq.question_2'),
      answer: i18n.t('pricing_plans.custom_bundles.faq.answer_2'),
    },
    {
      question: i18n.t('pricing_plans.custom_bundles.faq.question_3'),
      answer: i18n.t('pricing_plans.custom_bundles.faq.answer_3'),
    },
    {
      question: i18n.t('pricing_plans.custom_bundles.faq.question_4'),
      answer: i18n.t('pricing_plans.custom_bundles.faq.answer_4'),
    },
    {
      question: i18n.t('pricing_plans.custom_bundles.faq.question_5'),
      answer: i18n.t('pricing_plans.custom_bundles.faq.answer_5'),
    },
    {
      question: i18n.t('pricing_plans.custom_bundles.faq.question_6'),
      answer: (
        <Box gap="s32">
          <Text size="200">{i18n.t('pricing_plans.custom_bundles.faq.answer_6')}</Text>
          <Box alignItems="center">
            <Button
              onClick={async (e) => {
                e.stopPropagation()
                const { success } = await action()

                if (success) {
                  eventEmitter('Custom bundles FAQ')
                  setShowModal(true)
                }
              }}
            >
              {i18n.t('end_of_trial.talk_to_sales')}
            </Button>
          </Box>
        </Box>
      ),
    },
  ]

  return (
    <>
      {showModal && <ContactModal onClose={() => setShowModal(false)} />}
      <Box paddingX="s120">
        <Header />
        <Box gap="s16">
          {faqItems.map(({ question, answer }) => (
            <FaqItem key={question} question={question} answer={answer} />
          ))}
        </Box>
      </Box>
    </>
  )
}

export default PricingFaq
